import React, { useState, useEffect } from 'react';
import { getEventAnalytics } from '../../../services/eventService';
import { getMerchandiseStats } from '../../../services/merchandiseService';

const AnalyticsTab = ({ event }) => {
  const [analytics, setAnalytics] = useState(null);
  const [merchandiseStats, setMerchandiseStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (event?.id) {
      loadAnalytics();
    }
  }, [event?.id]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load event analytics and merchandise stats
      const [eventData, merchData] = await Promise.all([
        getEventAnalytics(event?.id)?.catch(() => null),
        getMerchandiseStats(event?.id)?.catch(() => null)
      ]);

      setAnalytics(eventData);
      setMerchandiseStats(merchData);
    } catch (error) {
      setError(`Failed to load analytics: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!event?.id) {
    return (
      <div className="p-6 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl text-muted-foreground">📊</span>
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">No Event Selected</h3>
        <p className="text-sm text-muted-foreground">
          Please create or select an event first to view analytics
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)]?.map((_, index) => (
              <div key={index} className="bg-muted rounded-lg p-4">
                <div className="h-4 bg-muted-foreground/20 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-muted-foreground/20 rounded w-1/2"></div>
              </div>
            ))}
          </div>
          <div className="bg-muted rounded-lg h-64"></div>
        </div>
      </div>
    );
  }

  // Mock analytics data for display (since we don't have real analytics yet)
  const mockAnalytics = {
    totalTicketsSold: 247,
    totalRevenue: 425000,
    averageTicketPrice: 1720,
    attendanceRate: 85,
    salesByDay: [
      { date: '2025-09-25', sales: 45 },
      { date: '2025-09-26', sales: 67 },
      { date: '2025-09-27', sales: 89 },
      { date: '2025-09-28', sales: 46 }
    ],
    ticketTypeBreakdown: [
      { name: 'General Admission', sold: 180, revenue: 270000 },
      { name: 'VIP', sold: 67, revenue: 155000 }
    ]
  };

  const displayAnalytics = analytics || mockAnalytics;
  const displayMerchStats = merchandiseStats || { total: 3, totalValue: 15000, totalStock: 800 };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Event Analytics</h2>
          <p className="text-sm text-muted-foreground">
            Performance metrics and sales data for {event?.title}
          </p>
        </div>
        <button
          onClick={loadAnalytics}
          className="text-sm text-primary hover:text-primary/80"
        >
          Refresh Data
        </button>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Tickets Sold</p>
              <p className="text-2xl font-bold text-foreground">{displayAnalytics?.totalTicketsSold?.toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600">🎫</span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-2xl font-bold text-foreground">₹{displayAnalytics?.totalRevenue?.toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600">💰</span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg. Ticket Price</p>
              <p className="text-2xl font-bold text-foreground">₹{displayAnalytics?.averageTicketPrice?.toLocaleString()}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600">📈</span>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Attendance Rate</p>
              <p className="text-2xl font-bold text-foreground">{displayAnalytics?.attendanceRate}%</p>
            </div>
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600">👥</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Chart */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Daily Sales Trend</h3>
        <div className="h-64 flex items-end space-x-4">
          {displayAnalytics?.salesByDay?.map((day, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div 
                className="bg-primary w-full rounded-t"
                style={{ height: `${(day?.sales / 100) * 100}%` }}
              ></div>
              <div className="text-xs text-muted-foreground mt-2">
                {new Date(day?.date)?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </div>
              <div className="text-xs font-medium text-foreground">{day?.sales}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Breakdown Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ticket Type Breakdown */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Ticket Type Performance</h3>
          <div className="space-y-3">
            {displayAnalytics?.ticketTypeBreakdown?.map((ticket, index) => (
              <div key={index} className="flex justify-between items-center p-3 bg-muted rounded">
                <div>
                  <div className="font-medium text-foreground">{ticket?.name}</div>
                  <div className="text-sm text-muted-foreground">{ticket?.sold} sold</div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-foreground">₹{ticket?.revenue?.toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Merchandise Stats */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">Merchandise Overview</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Total Items</span>
              <span className="font-medium text-foreground">{displayMerchStats?.total}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Inventory Value</span>
              <span className="font-medium text-foreground">₹{displayMerchStats?.totalValue?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Total Stock</span>
              <span className="font-medium text-foreground">{displayMerchStats?.totalStock}</span>
            </div>
            <div className="pt-3 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Stock levels and sales tracking help optimize inventory management
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-4">Export Reports</h3>
        <div className="flex gap-4">
          <button className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90">
            Export Sales Data (CSV)
          </button>
          <button className="px-4 py-2 border border-border rounded hover:bg-muted">
            Export Analytics Report (PDF)
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;